import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TourManagementChoice from "../components/tour/TourManagemetChoice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../app/store";
import { setPackageData } from "../reducer/tourReducer";
import Navbar from "../components/Navbar";
import { Paths } from "../router/Paths";
import useOnLoadPage from "../hooks/useOnLoadPage";
import { usePackage } from "../hooks/usePackage";
import Loader from "../components/common/Loader";
import { setActiveNavTab } from "../reducer/uiReducer";
import { addActiveTab } from "../reducer/sideBarSlice";
import { toast } from "react-toastify";
import { useUser } from "../hooks/useUser";

function TourManagementCreatePage() {
  const dispatch = useDispatch();
  const { packageCategories, packageData } = useSelector(
    (state: RootState) => state.tour
  );
  const { account } = useSelector((state: RootState) => state.account);
  const isAdmin = account?.role === "admin";
  const { getPackageCategories, createPrePackage } = usePackage();
  const { getFilteredSuppliers } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);
  const [createFor, setCreateFor] = useState<"self" | "supplier" | null>(null);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [supplierOptions, setSupplierOptions] = useState<any[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<any>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);
  const [skipNextSearch, setSkipNextSearch] = useState(false);

  const loadData = async () => {
    await getPackageCategories({ setLoading });
  };

  useEffect(() => {
    loadData();
  }, []);

  useOnLoadPage({ tabActive: "create" });

  const searchSuppliers = async (query: string) => {
    setSupplierOptions(
      [
        { _id: "1", name: "Supplier One" },
        { _id: "2", name: "Supplier Two" },
      ].filter((s) => s.name.toLowerCase().includes(query.toLowerCase()))
    );
  };

  useEffect(() => {
    if (skipNextSearch) {
      setSkipNextSearch(false);
      return;
    }

    const delayDebounce = setTimeout(async () => {
      if (supplierSearch.trim()) {
        setIsLoadingSuppliers(true);
        const response = await getFilteredSuppliers(
          1,
          10,
          supplierSearch.trim()
        );

        if (response?.suppliers) {
          setSupplierOptions(response?.suppliers);
          setShowDropdown(true);
        } else {
          setSupplierOptions([]);
          setShowDropdown(false);
        }

        setIsLoadingSuppliers(false);
      } else {
        setSupplierOptions([]);
        setShowDropdown(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [supplierSearch]);

  const onSelect = (option?: string) => {
    if (!option) return;
    dispatch(
      setPackageData({
        category: option,
      })
    );
  };

  const onContinue = () => {
    if (!packageData.category) {
      return;
    }
    if (isAdmin) {
      setShowModal(true);
    } else {
      navigate(Paths.tourManagementPackageName, {
        state: {},
      });
      dispatch(addActiveTab(Paths.tourManagementPackageName));
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setCreateFor(null);
    setSupplierSearch("");
    setSupplierOptions([]);
    setSelectedSupplier(null);
  };

  const handleConfirm = () => {
    if (createFor === "supplier" && !selectedSupplier) {
      toast.error("Please select a supplier");
      return;
    }

    const newPackageData: any = {
      category: packageData.category,
    };

    if (isAdmin && createFor === "supplier") {
      newPackageData.createdFor = selectedSupplier._id;
    }

    dispatch(setPackageData(newPackageData));

    setShowModal(false);
    navigate(Paths.tourManagementPackageName);
    dispatch(addActiveTab(Paths.tourManagementPackageName));
  };

  return (
    <>
      <div className={Classname.container}>
        <Navbar />
        <div className={Classname.content}>
          <span className="text-lg font-bold mb-2 md:text-xl lg:text-2xl">
            Create a new package
          </span>
          <span className="font-semibold mb-2 text-base md:text-lg">
            Which best describes your activity?
          </span>
          <p className="text-sm md:text-base mb-4 text-gray-600">
            This helps us categorize your product so customers can find it.
            Choose carefully as this is the only section that can't be changed
            later.
          </p>
          <Loader loading={loading} />
          <TourManagementChoice
            selected={packageData.category || ""}
            onSelect={onSelect}
            options={packageCategories}
            className="mb-10"
          />
          <div className={Classname.buttonContainer}>
            <button className={Classname.continueButton} onClick={onContinue}>
              Continue
            </button>
          </div>
        </div>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
              <h2 className="text-lg font-semibold mb-4">Create Package For</h2>
              <div className="mb-4">
                <label className="flex items-center mb-2">
                  <input
                    type="radio"
                    name="createFor"
                    value="self"
                    checked={createFor === "self"}
                    onChange={() => setCreateFor("self")}
                    className="mr-2"
                  />
                  Yourself
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="createFor"
                    value="supplier"
                    checked={createFor === "supplier"}
                    onChange={() => setCreateFor("supplier")}
                    className="mr-2"
                  />
                  For a Supplier
                </label>
              </div>
              {createFor === "supplier" && (
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">
                    Select Supplier
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-2"
                      placeholder="Search supplier..."
                      value={supplierSearch}
                      onChange={(e) => {
                        setSupplierSearch(e.target.value);
                      }}
                    />
                    {showDropdown && (
                      <div className="absolute left-0 right-0 z-10 bg-white max-h-32 overflow-y-auto border border-gray-200 rounded shadow">
                        {isLoadingSuppliers ? (
                          <div className="p-2 text-sm text-gray-500">
                            Loading...
                          </div>
                        ) : supplierOptions.length === 0 ? (
                          <div className="p-2 text-gray-400 text-sm">
                            No suppliers found
                          </div>
                        ) : (
                          supplierOptions.map((supplier) => (
                            <div
                              key={supplier._id}
                              className={`p-2 cursor-pointer hover:bg-gray-100 ${
                                selectedSupplier &&
                                selectedSupplier._id === supplier._id
                                  ? "bg-primary1 text-white"
                                  : ""
                              }`}
                              onClick={() => {
                                setSelectedSupplier(supplier);
                                setSkipNextSearch(true);
                                setSupplierSearch(
                                  `${supplier.firstName} ${supplier.lastName}`
                                );
                                setShowDropdown(false);
                                console.log("Selected Supplier:", supplier);
                              }}
                            >
                              {supplier.firstName} {supplier.lastName}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="flex justify-end space-x-2">
                <button
                  onClick={handleModalClose}
                  className="px-4 py-2 rounded text-sm bg-gray-200 text-gray-700 hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  className="px-4 py-2 rounded text-sm text-white bg-primary1"
                  disabled={
                    !createFor ||
                    (createFor === "supplier" && !selectedSupplier)
                  }
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default TourManagementCreatePage;

const Classname = {
  container:
    "flex w-full min-h-screen bg-white flex-col items-center font-main ",
  content: "flex w-full max-w-7xl flex-col flex-1 pt-28 max-sm:px-4",
  buttonContainer:
    "flex items-center justify-end w-full mt-10 md:mt-16 lg:mt-20",
  continueButton:
    "font-bold px-4 py-2 md:px-6 md:py-3 rounded-md text-white bg-primary1 transition duration-300 ease-in-out hover:bg-primary2",
};
